import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import PagePlaceholderImage from '@/images/placeholders/page-template.svg';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { isUserLanguageAvailableinMaterial } from '@/utils/helpers';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { Card } from 'primereact/card';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledImageCard = styled(Card)`
  &.p-card {
    max-width: 400px;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--gray-dark);
    border-radius: var(--medium-border-radius);
    box-shadow: none;
    overflow: hidden;
    cursor: pointer;

    &:hover,
    &:focus-within {
      .p-card-header .image-card-overlay {
        opacity: 1;
        cursor: pointer;
      }
    }

    &.disabled {
      opacity: 0.6;
      pointer-events: none !important;
    }

    .p-card-body {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: var(--small-padding);
      .p-card-title {
        margin-bottom: 0;
        font-size: var(--medium-font-size);
        line-height: var(--medium-line-height);
        color: var(--black-main);
      }
      .p-card-subtitle {
        margin-bottom: 0;
        font-size: var(--xsmall-font-size);
        line-height: var(--xsmall-line-height);
        color: var(--black-main);
      }
    }

    .p-card-header {
      position: relative;
      display: flex;
      align-items: stretch;
      aspect-ratio: 16 / 9;

      .image-card-thumbnail {
        background-size: contain;
        background-position: center center;
        background-repeat: no-repeat;
        background-color: var(--gray-main);
      }

      .image-card-overlay {
        position: absolute;
        height: 100%;
        padding: var(--small-padding);
        background: var(--orange-faint);
        opacity: 0;
        transition: all 0.4s;
      }
    }

    .p-card-footer {
      margin-top: auto;
    }

    label {
      cursor: pointer;
    }
  }
`;

const StyledPlayButton = styled(AppButton)`
  &.p-button.p-button-xs {
    height: var(--small-button-height) !important;
    width: var(--small-button-height) !important;
    padding: 0 0 0 3px;
  }
`;

export type ContentCardProps = {
  template: Course;
  additionalClass?: string;
  isDisabled?: boolean;
};

export const ContentCard: React.FC<ContentCardProps> = ({
  template,
  additionalClass,
  isDisabled,
  ...rest
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const currentUser = useAppSelector(selectCurrentUser);

  const [selectedLanguage, setSelectedLanguage] = useState<LanguagesEnum>(
    currentUser?.language &&
      isUserLanguageAvailableinMaterial(
        template.courseLanguages,
        currentUser?.language as LanguagesEnum,
      )
      ? (currentUser?.language as LanguagesEnum)
      : template.defaultLanguage || LanguagesEnum.EN,
  );

  useEffect(() => {
    setSelectedLanguage(
      currentUser?.language &&
        isUserLanguageAvailableinMaterial(
          template.courseLanguages,
          currentUser?.language as LanguagesEnum,
        )
        ? (currentUser?.language as LanguagesEnum)
        : template.defaultLanguage || LanguagesEnum.EN,
    );
  }, [currentUser?.language, template]);

  const navigateToCourseAttempt = async (e: Event) => {
    e.stopPropagation();

    try {
      const lastAttempt =
        await client.learnerCourses.getLearnerCourseLastAttempt(template.id);

      const attemptId = lastAttempt?.id;
      navigate(
        RedirectPaths[RedirectPathsEnum.DOCUMENT_VIEW](template.id, attemptId),
        { state: { from: location.pathname } },
      );
    } catch (e: any) {
      if (e?.statusCode === 404) {
        createLearnerCourseNewAttempt();
      } else {
        handleAxiosError(e as AxiosError, toast);
      }
    }
  };

  const createLearnerCourseNewAttempt = async () => {
    try {
      const newAttempt =
        await client.learnerCourses.createLearnerCourseNewAttempt(template.id);
      navigate(
        RedirectPaths[RedirectPathsEnum.DOCUMENT_VIEW](
          template.id,
          newAttempt.id,
        ),
        { state: { from: location.pathname } },
      );
    } catch (e: any) {
      handleAxiosError(e as AxiosError, toast);
    }
  };

  const className = classNames(
    {
      disabled: isDisabled,
    },
    additionalClass,
  );

  const header = (
    <label
      className="image-card-thumbnail flex-1"
      style={{
        backgroundImage: `url(${CSS.escape(
          template.courseThumbnail?.signedUrl ?? PagePlaceholderImage,
        )}`,
      }}
    />
  );

  const titleField = (
    <FlexContainer justify="space-between">
      <div>{template.name}</div>
      <StyledPlayButton
        icon="pi pi-caret-right"
        size="xs"
        severity="plain"
        className="align-self-end mt-auto"
        isDisabled={!selectedLanguage}
        onClick={(e: any) => navigateToCourseAttempt(e)}
      />
    </FlexContainer>
  );

  return (
    <StyledImageCard
      id={template.id}
      title={titleField}
      className={className}
      header={header}
      onClick={(e: any) => navigateToCourseAttempt(e)}
      {...rest}
    />
  );
};
